// Auth + profile context. Provides:
//   - the Firebase Auth user (or null)
//   - the user's Firestore profile doc (or null), kept live via onSnapshot
//   - a single `loading` flag that is true until both have settled.
//
// Why both in one context: every authed read in the app needs the user's
// profile (orgId at minimum). Coupling avoids a second context with its
// own loading state, which would force every guard to gate on two flags.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { onSnapshot } from 'firebase/firestore';
import { auth } from '../firebase';
import { userDoc } from '../lib/firestore';
import type { UserProfile } from '../lib/types';
import { setSentryUser } from '../sentry';

type AuthContextValue = {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [authResolved, setAuthResolved] = useState(false);
  const [profileResolved, setProfileResolved] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthResolved(true);
      // Sentry user context — uid + email only. Sign-out clears it so
      // subsequent errors aren't tagged with the previous user.
      setSentryUser(u ? { uid: u.uid, email: u.email } : null);
      // When the user signs out, the profile listener below tears down and
      // we reset profile to null. profileResolved goes back to true since
      // there's nothing to resolve for a signed-out user.
      if (!u) {
        setProfile(null);
        setProfileResolved(true);
      } else {
        // New user — profile is unknown until the snapshot fires.
        setProfileResolved(false);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(
      userDoc(user.uid),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Omit<UserProfile, 'createdAt'> & {
            createdAt: number | { toMillis(): number };
          };
          setProfile({
            ...data,
            createdAt:
              typeof data.createdAt === 'number'
                ? data.createdAt
                : data.createdAt.toMillis(),
          });
        } else {
          // Auth user exists but no profile doc — happens transiently during
          // signup before MFA enrolment writes the doc. Consumers should
          // route to onboarding rather than treat this as signed-out.
          setProfile(null);
        }
        setProfileResolved(true);
      },
      (err) => {
        console.error('[auth] profile snapshot failed', err);
        setProfile(null);
        setProfileResolved(true);
      },
    );
    return () => unsub();
  }, [user]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, profile, loading: !authResolved || !profileResolved }),
    [user, profile, authResolved, profileResolved],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
